import { computed, onMounted, readonly, shallowRef } from 'vue'

const interactionEvents = ['pointerdown', 'keydown', 'touchstart'] as const

const hasUserInteracted = shallowRef(false)
const isSoundEnabled = shallowRef(false)
const hasSoundControlInteracted = shallowRef(false)
let isTrackingInteraction = false

function removeInteractionTracking() {
  if (!isTrackingInteraction || typeof window === 'undefined') {
    return
  }

  interactionEvents.forEach((eventName) => {
    window.removeEventListener(eventName, handleDocumentInteraction, { capture: true })
  })
  isTrackingInteraction = false
}

function markUserInteracted() {
  if (hasUserInteracted.value) {
    return
  }

  hasUserInteracted.value = true
  removeInteractionTracking()
}

function handleDocumentInteraction() {
  window.setTimeout(markUserInteracted, 0)
}

function ensureInteractionTracking() {
  if (typeof window === 'undefined' || isTrackingInteraction || hasUserInteracted.value) {
    return
  }

  interactionEvents.forEach((eventName) => {
    window.addEventListener(eventName, handleDocumentInteraction, {
      capture: true,
      passive: true,
    })
  })
  isTrackingInteraction = true
}

export function useMediaSoundPreference() {
  const canPlayAudibly = computed(() => hasSoundControlInteracted.value && isSoundEnabled.value)
  const isPlaybackMuted = computed(() => !canPlayAudibly.value)

  function toggleSound() {
    const shouldEnableSound = !canPlayAudibly.value

    markUserInteracted()
    hasSoundControlInteracted.value = true
    isSoundEnabled.value = shouldEnableSound
  }

  onMounted(() => {
    ensureInteractionTracking()
  })

  return {
    canPlayAudibly,
    hasSoundControlInteracted: readonly(hasSoundControlInteracted),
    hasUserInteracted: readonly(hasUserInteracted),
    isPlaybackMuted,
    isSoundEnabled: readonly(isSoundEnabled),
    toggleSound,
  }
}

export function enableMediaSoundPreference() {
  isSoundEnabled.value = true
}

export function isMediaSoundPreferenceEnabled(): boolean {
  return isSoundEnabled.value
}

export function resetMediaSoundPreferenceForTest() {
  hasUserInteracted.value = false
  isSoundEnabled.value = false
  hasSoundControlInteracted.value = false
  removeInteractionTracking()
}
